module.exports = {
	"description": "A MenuItem represents an option in a [Menu](../menu).\nThey can be used to trigger actions or navigate to a new location.\nMenuItems provide context through optional variants, secondary text, or [Icons](../icon).\n\nA custom rendering hook is exposed to enable any extra functionality your app requires.",
	"methods": [],
	"props": {
		"children": {
			"flowType": {
				"name": "React$Node"
			},
			"required": false,
			"description": "Rendered content of the component"
		},
		"disabled": {
			"flowType": {
				"name": "boolean"
			},
			"required": false,
			"description": "Disable the menu item"
		},
		"iconEnd": {
			"flowType": {
				"name": "React$Element",
				"elements": [
					{
						"name": "unknown"
					}
				],
				"raw": "React$Element<*>"
			},
			"required": false,
			"description": "Icon that goes after the children"
		},
		"iconStart": {
			"flowType": {
				"name": "React$Element",
				"elements": [
					{
						"name": "unknown"
					}
				],
				"raw": "React$Element<*>"
			},
			"required": false,
			"description": "Icon that goes before the children"
		},
		"isHighlighted": {
			"flowType": {
				"name": "boolean"
			},
			"required": false,
			"description": "Display the item in an active style"
		},
		"item": {
			"flowType": {
				"name": "signature",
				"type": "object",
				"raw": "{\n  iconEnd?: React$Element<*>,\n  iconStart?: React$Element<*>,\n  disabled?: boolean,\n  divider?: boolean,\n  onClick?: (event: SyntheticEvent<>) => void,\n  render?: (item: Object, props: Object, theme: Object) => React$Element<*>,\n  secondaryText?: React$Node,\n  text?: React$Node,\n  variant?: 'regular' | 'danger' | 'success' | 'warning'\n}",
				"signature": {
					"properties": [
						{
							"key": "iconEnd",
							"value": {
								"name": "React$Element",
								"elements": [
									{
										"name": "unknown"
									}
								],
								"raw": "React$Element<*>",
								"required": false
							}
						},
						{
							"key": "iconStart",
							"value": {
								"name": "React$Element",
								"elements": [
									{
										"name": "unknown"
									}
								],
								"raw": "React$Element<*>",
								"required": false
							}
						},
						{
							"key": "disabled",
							"value": {
								"name": "boolean",
								"required": false
							}
						},
						{
							"key": "divider",
							"value": {
								"name": "boolean",
								"required": false
							}
						},
						{
							"key": "onClick",
							"value": {
								"name": "signature",
								"type": "function",
								"raw": "(event: SyntheticEvent<>) => void",
								"signature": {
									"arguments": [
										{
											"name": "event",
											"type": {
												"name": "SyntheticEvent",
												"elements": [],
												"raw": "SyntheticEvent<>"
											}
										}
									],
									"return": {
										"name": "void"
									}
								},
								"required": false
							}
						},
						{
							"key": "render",
							"value": {
								"name": "signature",
								"type": "function",
								"raw": "(item: Object, props: Object, theme: Object) => React$Element<*>",
								"signature": {
									"arguments": [
										{
											"name": "item",
											"type": {
												"name": "Object"
											}
										},
										{
											"name": "props",
											"type": {
												"name": "Object"
											}
										},
										{
											"name": "theme",
											"type": {
												"name": "Object"
											}
										}
									],
									"return": {
										"name": "React$Element",
										"elements": [
											{
												"name": "unknown"
											}
										],
										"raw": "React$Element<*>"
									}
								},
								"required": false
							}
						},
						{
							"key": "secondaryText",
							"value": {
								"name": "React$Node",
								"required": false
							}
						},
						{
							"key": "text",
							"value": {
								"name": "React$Node",
								"required": false
							}
						},
						{
							"key": "variant",
							"value": {
								"name": "union",
								"raw": "'regular' | 'danger' | 'success' | 'warning'",
								"elements": [
									{
										"name": "literal",
										"value": "'regular'"
									},
									{
										"name": "literal",
										"value": "'danger'"
									},
									{
										"name": "literal",
										"value": "'success'"
									},
									{
										"name": "literal",
										"value": "'warning'"
									}
								],
								"required": false
							}
						}
					]
				}
			},
			"required": false,
			"description": "Item data (see [example](../menu#data))"
		},
		"onClick": {
			"flowType": {
				"name": "signature",
				"type": "function",
				"raw": "(event: SyntheticEvent<>) => void",
				"signature": {
					"arguments": [
						{
							"name": "event",
							"type": {
								"name": "SyntheticEvent",
								"elements": [],
								"raw": "SyntheticEvent<>"
							}
						}
					],
					"return": {
						"name": "void"
					}
				}
			},
			"required": false,
			"description": "Called with the click event"
		},
		"render": {
			"flowType": {
				"name": "signature",
				"type": "function",
				"raw": "(item: Item, props: Object, theme: Object) => React$Element<*>",
				"signature": {
					"arguments": [
						{
							"name": "item",
							"type": {
								"name": "signature",
								"type": "object",
								"raw": "{\n  iconEnd?: React$Element<*>,\n  iconStart?: React$Element<*>,\n  disabled?: boolean,\n  divider?: boolean,\n  onClick?: (event: SyntheticEvent<>) => void,\n  render?: (item: Object, props: Object, theme: Object) => React$Element<*>,\n  secondaryText?: React$Node,\n  text?: React$Node,\n  variant?: 'regular' | 'danger' | 'success' | 'warning'\n}",
								"signature": {
									"properties": [
										{
											"key": "iconEnd",
											"value": {
												"name": "React$Element",
												"elements": [
													{
														"name": "unknown"
													}
												],
												"raw": "React$Element<*>",
												"required": false
											}
										},
										{
											"key": "iconStart",
											"value": {
												"name": "React$Element",
												"elements": [
													{
														"name": "unknown"
													}
												],
												"raw": "React$Element<*>",
												"required": false
											}
										},
										{
											"key": "disabled",
											"value": {
												"name": "boolean",
												"required": false
											}
										},
										{
											"key": "divider",
											"value": {
												"name": "boolean",
												"required": false
											}
										},
										{
											"key": "onClick",
											"value": {
												"name": "signature",
												"type": "function",
												"raw": "(event: SyntheticEvent<>) => void",
												"signature": {
													"arguments": [
														{
															"name": "event",
															"type": {
																"name": "SyntheticEvent",
																"elements": [],
																"raw": "SyntheticEvent<>"
															}
														}
													],
													"return": {
														"name": "void"
													}
												},
												"required": false
											}
										},
										{
											"key": "render",
											"value": {
												"name": "signature",
												"type": "function",
												"raw": "(item: Object, props: Object, theme: Object) => React$Element<*>",
												"signature": {
													"arguments": [
														{
															"name": "item",
															"type": {
																"name": "Object"
															}
														},
														{
															"name": "props",
															"type": {
																"name": "Object"
															}
														},
														{
															"name": "theme",
															"type": {
																"name": "Object"
															}
														}
													],
													"return": {
														"name": "React$Element",
														"elements": [
															{
																"name": "unknown"
															}
														],
														"raw": "React$Element<*>"
													}
												},
												"required": false
											}
										},
										{
											"key": "secondaryText",
											"value": {
												"name": "React$Node",
												"required": false
											}
										},
										{
											"key": "text",
											"value": {
												"name": "React$Node",
												"required": false
											}
										},
										{
											"key": "variant",
											"value": {
												"name": "union",
												"raw": "'regular' | 'danger' | 'success' | 'warning'",
												"elements": [
													{
														"name": "literal",
														"value": "'regular'"
													},
													{
														"name": "literal",
														"value": "'danger'"
													},
													{
														"name": "literal",
														"value": "'success'"
													},
													{
														"name": "literal",
														"value": "'warning'"
													}
												],
												"required": false
											}
										}
									]
								}
							}
						},
						{
							"name": "props",
							"type": {
								"name": "Object"
							}
						},
						{
							"name": "theme",
							"type": {
								"name": "Object"
							}
						}
					],
					"return": {
						"name": "React$Element",
						"elements": [
							{
								"name": "unknown"
							}
						],
						"raw": "React$Element<*>"
					}
				}
			},
			"required": false,
			"description": "Custom render function"
		},
		"secondaryText": {
			"flowType": {
				"name": "React$Node"
			},
			"required": false,
			"description": "Secondary text"
		},
		"tabIndex": {
			"flowType": {
				"name": "number"
			},
			"required": false,
			"description": "Determines if the item can be focused",
			"defaultValue": {
				"value": "0",
				"computed": false
			}
		},
		"variant": {
			"flowType": {
				"name": "union",
				"raw": "'regular' | 'danger' | 'success' | 'warning'",
				"elements": [
					{
						"name": "literal",
						"value": "'regular'"
					},
					{
						"name": "literal",
						"value": "'danger'"
					},
					{
						"name": "literal",
						"value": "'success'"
					},
					{
						"name": "literal",
						"value": "'warning'"
					}
				]
			},
			"required": false,
			"description": "Available variants",
			"defaultValue": {
				"value": "'regular'",
				"computed": false
			}
		}
	}
}


//////////////////
// WEBPACK FOOTER
// ./node_modules/react-docgen-loader!./src/Menu/MenuItem.js
// module id = 1681
// module chunks = 0